import { Component, Input, AfterViewInit, OnChanges, SimpleChanges, ViewChild, ElementRef, OnDestroy } from '@angular/core';
declare var require: any;
const echarts = require('echarts/lib/echarts');
require('echarts/lib/chart/gauge');

@Component({
  selector: 'app-gauge-chart',
  template: `<div #chartContainer></div>`,
  styles: [':host { display: block; }'],
  standalone: true
})
export class GaugeChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef<HTMLDivElement>;

  @Input() EnergyMeter: boolean = false;
  @Input() canvasWidth: number = 400;
  @Input() canvasHeight: number = 400;
  @Input() needleValue: any = 70;
  @Input() centralLabel: string = '';
  @Input() bottomLabel: string = '';
  @Input() name: string = '';
  @Input() color: string = "";
  @Input() options: any = {
    arcColors: ['#72FF13', '#FAA700', '#ff2a1b'], // default to 3 colors
    arcDelimiters: [30, 74],
    arcLabels: [],    // e.g. [4.5, 11.2]
    hasNeedle: true,
    needleColor: '#72FF13',
    needleStartValue: 0,
    needleUpdateSpeed: '',
    rangeLabel: ['0', '15']
  };
  @Input() bottomLabelColor: string = "#000000";
  @Input() nameColor: string = "#444444";
  @Input() arcLabelColor: string = "#000";
  @Input() rangeLabelColor: string = "#000";
  @Input() Piezo: boolean = false;


  private myChart: any;

  ngAfterViewInit(): void {
    const c = this.chartContainer.nativeElement;
    c.style.width = `${this.canvasWidth}px`;
    c.style.height = `${this.canvasHeight}px`;
    const ratio = window.devicePixelRatio * 2 || 2.5;
    this.myChart = echarts.init(c, 'light', { devicePixelRatio: ratio });

    this.updateChart();
    // console.log(this.options);
    // console.log(this.bottomLabel);
    // console.log(this.needleValue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.myChart && (
      changes['needleValue'] ||
      changes['centralLabel'] ||
      changes['bottomLabel'] ||
      changes['options'] ||
      changes['name'] ||
      changes['bottomLabelColor'] ||
      changes['nameColor'] ||
      changes['arcLabelColor'] ||
      changes['rangeLabelColor']
    )) {
      this.updateChart();
    }
  }

  private updateChart(): void {
    this.renderGauge();
  }

  private renderGauge(): void {
    const r = this.options.rangeLabel || ['0', '100'];
    const minVal = Number(r[0]) || 0;
    const maxVal = Number(r[1]) || 100;

    // Get arc colors and delimiters from options
    const colors: string[] = this.options.arcColors || ['#67e0e3', '#37a2da', '#fd666d'];
    const delimiters: number[] = this.options.arcDelimiters || [30, 74];

    // Dynamically generate axisColorSegments based on the colors and delimiters.
    let axisColorSegments: any[] = [];
    if (colors.length > 3 && delimiters.length === colors.length - 1) {
      axisColorSegments.push([delimiters[0] / 100, colors[0]]);
      for (let i = 1; i < delimiters.length; i++) {
        axisColorSegments.push([delimiters[i] / 100, colors[i]]);
      }
      axisColorSegments.push([1, colors[colors.length - 1]]);
    } else {
      if (delimiters.length >= 2) {
        if(this.name=='Power Factor'){
          const proportionalPositions = this.options.arcLabels.map(value => (((Number(value) - minVal) / (maxVal - minVal))).toFixed(1));
          axisColorSegments =[
            [Number(proportionalPositions[0]), colors[0]],  // Red (0.6 to 0.7)
            [Number(proportionalPositions[1]), colors[1]],  // Orange (0.7 to 0.8)
            [maxVal, colors[2]]   // Extend green to max (1.0 to 1.5)
          ]
        }else{

          if(this.name=='Flow' || this.name=='Pressure'){
            axisColorSegments = [
              [delimiters[0] / maxVal, colors[0]],
              [delimiters[1] / maxVal, colors[1]],
              [1, colors[2]]
          ];
          }else{
            axisColorSegments = [
              [delimiters[0] / 100, colors[0]],
              [delimiters[1] / 100, colors[1]],
              [1, colors[2]]
            ];
          }
        }
      } else {
        axisColorSegments = [
          [0.3, colors[0]],
          [0.7, colors[1]],
          [1, colors[2]]
        ];
      }
    }

    // Compute needle color if arcLabels are provided.
    /* `computedNeedleColor` is a variable used to determine the color of the needle in the gauge
    chart based on the provided `needleValue` and the `arcLabels` specified in the options. */

    let computedNeedleColor = this.options.needleColor;
    const arcLabels: any[] = this.options.arcLabels || [];
    if (arcLabels.length >= 2 && arcLabels.length < 5) {
      const [lab0, lab1] = arcLabels.map(Number);
      const val = +this.needleValue;
      if (val < lab0) computedNeedleColor = colors[0];
      else if (val < lab1) computedNeedleColor = colors[1];
      else computedNeedleColor = colors[2];
    }else if(arcLabels.length ==5) {
      const [lab0, lab1,lab2,lab3,lab4] = arcLabels.map(Number);
      const val = parseInt(this.needleValue);
      if (val < lab0) computedNeedleColor = colors[0];
      else if (val < lab1) computedNeedleColor = colors[1];
      else if (val < lab2) computedNeedleColor = colors[2];
      else if (val < lab3) computedNeedleColor = colors[3];
      else if (val < lab4) computedNeedleColor = colors[4];
      else computedNeedleColor = colors[5];
    }
    // Hide arc and needle when bottomLabel is exactly "N/A"
    const showArc = this.bottomLabel !== "N/A";

    // Only show needle if enabled in options and if arc is visible.
    const pointerConfig = this.options.hasNeedle && showArc
      ? { width: 3, length: '80%', itemStyle: { color: computedNeedleColor } }
      : { show: false };

    // Collect values that should be labeled (min, max, and any arcLabels)
    const labelSet = [minVal, maxVal, ...arcLabels.map(Number)];
    const totalTicks = (maxVal - minVal) * 10 || 10;

    // Main gauge series always renders the detail section with bottom label.
    const mainSeries: any = {
      type: 'gauge',
      min: minVal,
      max: maxVal,
      startAngle: 180,
      endAngle: 0,
      center: ['50%', '55%'],
      radius: '80%',
      splitNumber: showArc ? totalTicks : 0,
      axisLine: showArc ? { lineStyle: { width: 13, color: axisColorSegments } } : { show: false },
      axisTick: showArc
        ? { show: true, splitNumber: 1, lineStyle: { color: '#fff', width: 2 } }
        : { show: false },
      splitLine: { show: false },
      axisLabel: showArc
        ? {
            show: true,
            interval: 0,
            distance: this.EnergyMeter ? -40 : this.Piezo ? -42 : -35,
            fontSize: this.EnergyMeter ? 11 : this.Piezo ? 15: 12,
            fontWeight: 'bold',
            color: this.arcLabelColor,
            formatter: (val: number) => {
              const tol = 1e-3;
              if (Math.abs(val - minVal) < tol || Math.abs(val - maxVal) < tol) {
                return '';
              }
              if (labelSet.some(x => Math.abs(x - val) < tol)) {
                return String(val);
              }
              return '';
            }
          }
        : { show: false },
      pointer: pointerConfig,
      detail: {
        offsetCenter: [0, '70%'],
        fontSize: 10,
        valueAnimation: true,
        formatter: (p: any) => {
          // If bottomLabel is empty or exactly "N/A", use the current gauge value.
          if (!this.bottomLabel || this.bottomLabel === 'N/A') {
            return `{value|${this.bottomLabel}}\n{title|${this.name}}`;
          }

            return `{value|${this.bottomLabel}}\n{title|${this.name}}`;

        },
        rich: {
          value: { fontWeight: 'bold', fontSize: 16, color: this.bottomLabelColor, lineHeight: 20 },
          title: { fontWeight: 'bold', color: this.nameColor, fontSize: 16, lineHeight: 16 }
        }
      },
      data: [
        {
          value: +this.needleValue,
          name: this.centralLabel
        }
      ]
    };

    // Overlay min/max series only when the arc is shown.
    const seriesArr = [mainSeries];
    if (showArc) {
      const minMaxSeries = {
        type: 'gauge',
        min: minVal,
        max: maxVal,
        startAngle: 180,
        endAngle: 0,
        center: ['50%', '63%'],
        radius: '80%',
        splitNumber: totalTicks,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          show: true,
          interval: 0,
          distance: -22,
          fontSize: this.Piezo ? 15: 10,
          fontWeight: 'bold',
          color: this.rangeLabelColor,
          formatter: (val: number) => {
            const tol = 1e-3;
            if (Math.abs(val - minVal) < tol || Math.abs(val - maxVal) < tol) {
              return String(val);
            }
            return '';
          }
        },
        pointer: { show: false },
        detail: { show: false }
      };
      seriesArr.push(minMaxSeries);
    }

    // Add tooltip configuration so that when hovering anywhere on the gauge,
    // a custom message is shown below the gauge.
    const option = {
      // tooltip: {
      //   trigger: 'item',
      //   triggerOn: 'mousemove',
      //   formatter: function (params) {
      //     return 'This is your custom message!'; // Replace with your custom message
      //   },
      //   position: function (point, params, dom, rect, size) {
      //     // Position the tooltip below the gauge
      //     return [(size.viewSize[0] - size.contentSize[0]) / 2, size.viewSize[1] + 10];
      //   },
      //   backgroundColor: 'rgba(50,50,50,0.7)',
      //   textStyle: {
      //     color: '#fff'
      //   }
      // },
      series: seriesArr
    };


    this.myChart.setOption(option);
  }




  ngOnDestroy(): void {
    if (this.myChart) {
      this.myChart.dispose();
    }
  }
}
